/**
 * G2 Color Picker - Shopify Integration
 * 
 * Modal-based color picker that lets customers pick any color
 * and find the closest matching OEM paint codes.
 * 
 * Works alongside the Vehicle Finder as an alternative selection method.
 */

(function() {
  'use strict';

  const API_BASE_URL = window.G2_API_BASE_URL || 'https://api.g2usa.com';

  // LocalStorage keys (shared with vehicle finder)
  const STORAGE_KEYS = {
    year: 'g2_vehicle_year',
    make: 'g2_vehicle_make',
    model: 'g2_vehicle_model',
    colorCode: 'g2_vehicle_color_code',
    colorName: 'g2_vehicle_color_name',
    colorHex: 'g2_vehicle_color_hex'
  };

  let selectedColor = {
    colorCode: '',
    colorName: '',
    colorHex: '',
    year: '',
    make: '',
    model: ''
  };

  let modal = null;
  let modalOverlay = null;
  let colorPickerInput = null;
  let hexInput = null;
  let findBtn = null;
  let resultsContainer = null;
  let chooseBtn = null;

  function init() {
    if (window.G2ColorPickerInitialized) return;
    window.G2ColorPickerInitialized = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAfterDOMReady);
    } else {
      initAfterDOMReady();
    }
  }

  function initAfterDOMReady() {
    modal = document.getElementById('g2-color-picker-modal');
    modalOverlay = document.getElementById('g2-cp-modal-overlay');

    if (!modal || !modalOverlay) return;

    colorPickerInput = document.getElementById('g2-cp-color-input');
    hexInput = document.getElementById('g2-cp-hex-input');
    findBtn = document.getElementById('g2-cp-find-btn');
    resultsContainer = document.getElementById('g2-cp-results');
    chooseBtn = document.getElementById('g2-cp-choose-btn');

    // Trigger buttons
    document.querySelectorAll('[data-g2-cp-trigger]').forEach(function(btn) {
      btn.addEventListener('click', openModal);
    });

    // Close handlers
    document.querySelectorAll('[data-g2-cp-close]').forEach(function(btn) {
      btn.addEventListener('click', closeModal);
    });

    if (modalOverlay) {
      modalOverlay.addEventListener('click', closeModal);
    }

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('active')) {
        closeModal();
      }
    });

    // Color picker sync
    if (colorPickerInput) {
      colorPickerInput.addEventListener('input', function(e) {
        if (hexInput) hexInput.value = e.target.value;
      });
    }

    if (hexInput) {
      hexInput.addEventListener('input', function(e) {
        var val = e.target.value;
        if (!val.startsWith('#')) val = '#' + val;
        if (/^#[0-9A-Fa-f]{6}$/.test(val) && colorPickerInput) {
          colorPickerInput.value = val;
        }
      });

      hexInput.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') findSimilarColors();
      });
    }

    if (findBtn) {
      findBtn.addEventListener('click', findSimilarColors);
    }

    if (chooseBtn) {
      chooseBtn.addEventListener('click', handleChooseColor);
    }
  }

  function openModal(e) {
    if (e) e.preventDefault();
    if (modal) modal.classList.add('active');
    if (modalOverlay) modalOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  function closeModal(e) {
    if (e) e.preventDefault();
    if (modal) modal.classList.remove('active');
    if (modalOverlay) modalOverlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  async function findSimilarColors() {
    var hex = (hexInput ? hexInput.value : '#3498db').replace('#', '');
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return;

    if (resultsContainer) {
      resultsContainer.innerHTML = '<div class="g2-loading">Searching for similar colors...</div>';
    }
    if (findBtn) findBtn.disabled = true;

    // Clear previous selection
    selectedColor = { colorCode: '', colorName: '', colorHex: '', year: '', make: '', model: '' };
    updateChooseBtn();

    try {
      var response = await fetch(API_BASE_URL + '/api/find-similar-colors?hex=' + encodeURIComponent(hex));
      if (!response.ok) throw new Error('Failed to fetch');
      var data = await response.json();
      displayResults(data.results || []);
    } catch (err) {
      console.error('G2 Color Picker: Error finding colors:', err);
      if (resultsContainer) {
        resultsContainer.innerHTML = '<div class="g2-error">Error finding colors. Please try again.</div>';
      }
    } finally {
      if (findBtn) findBtn.disabled = false;
    }
  }

  function displayResults(colors) {
    if (!resultsContainer) return;
    resultsContainer.innerHTML = '';

    if (!colors || colors.length === 0) {
      resultsContainer.innerHTML = '<div class="g2-empty">No matching colors found. Try a different color.</div>';
      return;
    }

    colors.forEach(function(color) {
      var swatch = document.createElement('div');
      swatch.className = 'g2-color-swatch';
      swatch.setAttribute('data-color-code', color.color_code);
      swatch.setAttribute('data-color-name', color.color_name);
      swatch.setAttribute('data-color-hex', color.color_hex);
      swatch.setAttribute('data-year', color.year || '');
      swatch.setAttribute('data-make', color.make || '');
      swatch.setAttribute('data-model', color.model || '');

      swatch.innerHTML =
        '<div class="g2-swatch-color" style="background-color: #' + color.color_hex + '"></div>' +
        '<div class="g2-swatch-info">' +
          '<div class="g2-swatch-code">' + color.color_code + '</div>' +
          '<div class="g2-swatch-name">' + color.color_name + '</div>' +
        '</div>';

      swatch.addEventListener('click', function() {
        selectColor(color);
      });

      resultsContainer.appendChild(swatch);
    });
  }

  function selectColor(color) {
    selectedColor.colorCode = color.color_code;
    selectedColor.colorName = color.color_name;
    selectedColor.colorHex = color.color_hex;
    selectedColor.year = color.year || '';
    selectedColor.make = color.make || '';
    selectedColor.model = color.model || '';

    // Highlight selected swatch
    if (resultsContainer) {
      resultsContainer.querySelectorAll('.g2-color-swatch').forEach(function(s) {
        s.classList.remove('selected');
      });
      var sel = resultsContainer.querySelector('[data-color-code="' + color.color_code + '"]');
      if (sel) sel.classList.add('selected');
    }

    updateChooseBtn();
  }

  function updateChooseBtn() {
    if (!chooseBtn) return;
    if (selectedColor.colorCode) {
      chooseBtn.disabled = false;
      chooseBtn.textContent = 'Choose This Color (' + selectedColor.colorCode + ')';
      chooseBtn.classList.add('ready');
    } else {
      chooseBtn.disabled = true;
      chooseBtn.textContent = 'Pick a Color Above';
      chooseBtn.classList.remove('ready');
    }
  }

  function handleChooseColor(e) {
    if (e) e.preventDefault();
    if (!selectedColor.colorCode) return;

    // Save into shared localStorage so vehicle finder display picks it up
    try {
      localStorage.setItem(STORAGE_KEYS.colorCode, selectedColor.colorCode);
      localStorage.setItem(STORAGE_KEYS.colorName, selectedColor.colorName);
      localStorage.setItem(STORAGE_KEYS.colorHex, selectedColor.colorHex);
      localStorage.setItem(STORAGE_KEYS.year, selectedColor.year);
      localStorage.setItem(STORAGE_KEYS.make, selectedColor.make);
      localStorage.setItem(STORAGE_KEYS.model, selectedColor.model);
    } catch (err) { /* ignore */ }

    // Populate Shopify form fields
    var yearField = document.getElementById('year');
    var makeField = document.getElementById('make');
    var modelField = document.getElementById('model');
    var colorField = document.getElementById('color');

    if (yearField) yearField.value = selectedColor.year;
    if (makeField) makeField.value = selectedColor.make;
    if (modelField) modelField.value = selectedColor.model;
    if (colorField) colorField.value = selectedColor.colorCode + (selectedColor.colorName ? ' - ' + selectedColor.colorName : '');

    [yearField, makeField, modelField, colorField].forEach(function(field) {
      if (field) field.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Update the vehicle finder selected display (reuse its UI)
    updateSelectedDisplay();

    // Visual feedback
    if (chooseBtn) {
      var origText = chooseBtn.textContent;
      chooseBtn.textContent = '✓ Color Added!';
      chooseBtn.style.backgroundColor = '#28a745';
      setTimeout(function() {
        chooseBtn.textContent = origText;
        chooseBtn.style.backgroundColor = '';
      }, 1000);
    }

    setTimeout(function() {
      closeModal();
    }, 1200);
  }

  function updateSelectedDisplay() {
    var initialTrigger = document.getElementById('g2-initial-trigger');
    var cpTrigger = document.getElementById('g2-cp-trigger-btn');
    var orSeparator = document.getElementById('g2-or-separator');
    var selectedDisplay = document.getElementById('g2-selected-display');
    var displayVehicle = document.getElementById('g2-display-vehicle');
    var displayColorName = document.getElementById('g2-display-color-name');
    var displayColorBox = document.getElementById('g2-display-color-box');
    var triggerHelper = initialTrigger ? initialTrigger.previousElementSibling : null;
    if (triggerHelper && !triggerHelper.classList.contains('g2-trigger-helper')) triggerHelper = null;

    if (selectedColor.colorCode) {
      if (initialTrigger) initialTrigger.style.display = 'none';
      if (cpTrigger) cpTrigger.style.display = 'none';
      if (orSeparator) orSeparator.style.display = 'none';
      if (triggerHelper) triggerHelper.style.display = 'none';
      if (selectedDisplay) selectedDisplay.classList.add('show');

      if (displayVehicle) {
        var vehicleText = [selectedColor.year, selectedColor.make, selectedColor.model].filter(Boolean).join(' ');
        displayVehicle.textContent = vehicleText || 'Custom Color';
      }
      if (displayColorName) {
        displayColorName.textContent = selectedColor.colorCode + ' - ' + selectedColor.colorName;
      }
      if (displayColorBox && selectedColor.colorHex) {
        displayColorBox.style.backgroundColor = '#' + selectedColor.colorHex;
      }
    }
  }

  // Expose public API
  window.G2ColorPicker = {
    openModal: openModal,
    closeModal: closeModal,
    getSelectedColor: function() { return Object.assign({}, selectedColor); }
  };

  init();
})();
